'use client'

// Memory loop — before / the written note / after, played as a small state
// machine. Ported from apps/frontend/design/legix-website-v2/Website.dc.html
// (MEMORY LOOP block of the data-dc-script + the `.ml-sec` template). The
// beats and the note body ship verbatim in public/landing/memory/data.js
// (window.MEMLOOP).
//
// Stages: 0 nothing · 1 March · 2 writing · 3 saved · 4 April. The section
// sits mid-page, so the loop waits to be seen (IntersectionObserver) rather
// than running through its only pass while it is still below the fold.

import { Component } from 'react'
import { Timers, memLoop, prefersReduced } from './landing-live'

const ML_TO_WRITE_MS = 2200
const ML_CHAR_MS = 17
const ML_TO_AFTER_MS = 1100
const ML_HOLD_MS = 7000

interface MemoryState {
  mlStage: number
  mlTyped: number
}

export class MemorySection extends Component<Record<string, never>, MemoryState> {
  override state: MemoryState = { mlStage: 0, mlTyped: 0 }

  private timers = new Timers()
  private io: IntersectionObserver | null = null
  private reduced = false

  override componentDidMount(): void {
    this.reduced = prefersReduced()
    const node = document.querySelector('.ml-sec')
    if (!node || typeof IntersectionObserver === 'undefined') {
      this.mlBegin()
      return
    }
    this.io = new IntersectionObserver(
      (ents) => {
        for (const e of ents) {
          if (e.isIntersecting) {
            this.io?.disconnect()
            this.io = null
            this.mlBegin()
            break
          }
        }
      },
      { threshold: 0.3 },
    )
    this.io.observe(node)
  }

  override componentWillUnmount(): void {
    this.timers.clear()
    if (this.io) {
      this.io.disconnect()
      this.io = null
    }
  }

  private at(ms: number, fn: () => void): void {
    this.timers.at(ms, fn)
  }

  private mlBegin(): void {
    // the data script is a separate request; re-ask until it has landed
    if (!memLoop()) {
      this.at(60, () => this.mlBegin())
      return
    }
    if (this.reduced) {
      this.setState({ mlStage: 4, mlTyped: memLoop()?.memory.body.length ?? 0 })
      return
    }
    this.setState({ mlStage: 0, mlTyped: 0 })
    this.at(600, () => this.setState({ mlStage: 1 }))
    this.at(600 + ML_TO_WRITE_MS, () => {
      this.setState({ mlStage: 2 })
      this.mlType()
    })
  }

  private mlType(): void {
    const full = memLoop()?.memory.body.length ?? 0
    const step = () => {
      const n = this.state.mlTyped + 1
      this.setState({ mlTyped: n })
      if (n < full) this.at(ML_CHAR_MS, step)
      else
        this.at(700, () => {
          this.setState({ mlStage: 3 })
          this.at(ML_TO_AFTER_MS, () => {
            this.setState({ mlStage: 4 })
            this.at(ML_HOLD_MS, () => this.at(700, () => this.mlBegin()))
          })
        })
    }
    step()
  }

  override render() {
    const ML = memLoop()
    const mlStage = this.state.mlStage
    const b = ML?.beats.find((x) => x.id === 'before')
    const a = ML?.beats.find((x) => x.id === 'after')
    const wait = (on: boolean) => 'ml-beat' + (on ? '' : ' ml-waiting')

    const railCls = 'ml-rail' + (mlStage >= 1 ? ' is-live' : '')
    const beforeCls = wait(mlStage >= 1)
    const noteCls = wait(mlStage >= 2) + ' is-note'
    const afterCls = wait(mlStage >= 4) + ' is-after'
    const saved = mlStage >= 3
    const empty = mlStage < 2
    const typing = mlStage === 2
    const narration =
      ML && mlStage >= 3
        ? 'Memory added · ' + ML.memory.scopeLabel
        : ML
          ? ML.memory.author + ' is adding a correction'
          : ''
    const noteText = ML
      ? mlStage >= 3
        ? ML.memory.body
        : ML.memory.body.slice(0, this.state.mlTyped)
      : ''
    const modalCls = 'ml-modal' + (mlStage < 2 ? ' is-idle' : '')
    const authorCls = 'ml-author' + (mlStage >= 3 ? ' is-on' : '')
    const saveCls =
      'ml-save' + (mlStage >= 2 ? ' is-armed' : '') + (mlStage >= 3 ? ' is-saved' : '')
    const saveText = mlStage >= 3 ? '✓ Saved' : 'Save'

    return (
      <div className="ml-sec" data-screen-label="Memory" data-slot="landing-memory">
        <div className="ml-wrap">
          <header className="ml-head">
            <h2 className="ml-h2">Teach it how your firm thinks.</h2>
            <p className="ml-sub">
              Rules can hold a threshold. They can’t hold a reason. Write the reason once and your
              judgment is applied going forward.
            </p>
          </header>

          <div className={railCls}>
            {/* 1 · the ordinary reading */}
            <div className={beforeCls}>
              <span className="ml-dot"></span>
              <div className="ml-lbl">{b?.month}</div>
              <div className="ml-card">
                <div className="ml-row">
                  <span className="ml-raw">{b?.raw}</span>
                  <span className="ml-chip is-ordinary">{ML && b?.to ? ML.acct(b.to) : ''}</span>
                  <span className="ml-amt">{ML && b?.amt != null ? ML.money(b.amt) : ''}</span>
                </div>
                <div className="ml-why">
                  <div className="ml-whyhead">
                    <span className="ml-kicker">Reasoning</span>
                  </div>
                  <p className="ml-whytext">{b?.reason}</p>
                </div>
              </div>
            </div>

            {/* 2 · the correction, written by a person */}
            <div className={noteCls}>
              <span className="ml-dot"></span>
              <div className="ml-lbl">
                {saved ? <span className="ml-tick">✓</span> : null}
                {narration}
              </div>
              <div className={modalCls}>
                <div className="ml-mhead">
                  <span className="ml-mmark">◆</span>
                  <span className="ml-mtitle">New memory</span>
                  <span className="ml-scope">
                    {ML?.memory.scopeLabel} <span className="ml-caret">▼</span>
                  </span>
                </div>
                <div className="ml-mbody">
                  {empty ? <span className="ml-ph">What should the books remember?</span> : null}
                  {noteText}
                  {typing ? <span className="ml-cursor"></span> : null}
                </div>
                <div className="ml-mfoot">
                  <span className={authorCls}>
                    {ML ? ML.memory.author + ' · ' + ML.memory.added : ''}
                  </span>
                  <span className={saveCls}>
                    {saveText} <span className="ml-ret">⏎</span>
                  </span>
                </div>
              </div>
            </div>

            {/* 3 · the same situation, decided differently */}
            <div className={afterCls}>
              <span className="ml-dot"></span>
              <div className="ml-lbl">{a?.month}</div>
              <div className="ml-card">
                <div className="ml-row">
                  <span className="ml-raw">{a?.raw}</span>
                  <span className="ml-chip">{ML && a?.to ? ML.acct(a.to) : ''}</span>
                  <span className="ml-amt">{ML && a?.amt != null ? ML.money(a.amt) : ''}</span>
                </div>
                <div className="ml-why">
                  <div className="ml-whyhead">
                    <span className="ml-kicker">Reasoning</span>
                  </div>
                  <p className="ml-whytext">{a?.reason}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
